import { MailCheck } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

import { ApiError } from '@/api/client';
import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { Spinner } from '@/components/ui/Spinner';
import { useT } from '@/lib/i18n';

import { resendVerificationCode, verifyEmailCode } from '../api/auth';

const CODE_LENGTH = 6;

/** The fallback cooldown, used only when the server did not say. */
const DEFAULT_RESEND_SECONDS = 60;

function messageFor(error: unknown, fallback: string): string {
  return error instanceof ApiError ? error.message : fallback;
}

export interface EmailCodeFormProps {
  /** The address the code went to. Shown, and sent back with the code. */
  email: string;
  /**
   * Seconds before the resend link wakes up, as the server reported them when
   * it sent the code. Zero starts the link live, which is right for somebody
   * arriving on this screen later rather than straight from registering.
   */
  resendAfter?: number;
  /** Called once the address is confirmed. */
  onVerified: () => Promise<void> | void;
}

/**
 * Enter the six digits we emailed, or ask for six more.
 *
 * Everything here exists because of how this screen fails in practice. The
 * code is auto-submitted on the sixth digit, because nobody wants to press a
 * button after typing a number they just read. A wrong code says how many
 * tries are left, because "incorrect" twice in a row reads as a broken server
 * rather than a mistyped digit. The resend link counts down from the cooldown
 * the *server* is enforcing, so it is never live a moment before the server
 * will honour it - a resend that fails with "too soon" is the fastest way to
 * make somebody believe the email is never coming.
 */
export function EmailCodeForm({ email, resendAfter = 0, onVerified }: EmailCodeFormProps) {
  const t = useT();
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [verifying, setVerifying] = useState(false);
  const [resending, setResending] = useState(false);
  const [resendIn, setResendIn] = useState(resendAfter);
  const inputRef = useRef<HTMLInputElement>(null);
  // The last code submitted, so auto-submit does not fire twice for one entry.
  const submitted = useRef<string | null>(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  useEffect(() => {
    if (resendIn <= 0) return;
    const timer = setTimeout(() => setResendIn((seconds) => seconds - 1), 1000);
    return () => clearTimeout(timer);
  }, [resendIn]);

  const verify = async (value: string) => {
    if (value.length !== CODE_LENGTH || submitted.current === value) return;
    submitted.current = value;
    setError(null);
    setNotice(null);
    setVerifying(true);
    try {
      await verifyEmailCode(email, value);
      await onVerified();
    } catch (caught) {
      submitted.current = null;
      if (caught instanceof ApiError && caught.code === 'CODE_INVALID') {
        const left = caught.details?.attempts_left;
        setError(
          typeof left === 'number'
            ? t('auth.code.wrong', { count: left })
            : caught.message,
        );
      } else {
        setError(messageFor(caught, t('auth.common.generic_error')));
      }
      setCode('');
      inputRef.current?.focus();
    } finally {
      setVerifying(false);
    }
  };

  const resend = async () => {
    setError(null);
    setNotice(null);
    setResending(true);
    try {
      const sent = await resendVerificationCode(email);
      setNotice(t('auth.code.resent', { email }));
      setResendIn(sent.resend_after || DEFAULT_RESEND_SECONDS);
      setCode('');
      submitted.current = null;
      inputRef.current?.focus();
    } catch (caught) {
      // The server knows exactly how long is left on the cooldown, so its
      // number replaces ours rather than being shown beside it.
      const retry = caught instanceof ApiError ? caught.details?.retry_after : undefined;
      if (typeof retry === 'number') setResendIn(retry);
      setError(messageFor(caught, t('auth.common.generic_error')));
    } finally {
      setResending(false);
    }
  };

  return (
    <form
      onSubmit={(event) => {
        event.preventDefault();
        if (code.length !== CODE_LENGTH) {
          setError(t('auth.code.enter_digits', { length: CODE_LENGTH }));
          return;
        }
        void verify(code);
      }}
      noValidate
      className="space-y-4"
    >
      {notice ? <Alert tone="success">{notice}</Alert> : null}

      <div>
        <label htmlFor="email-code" className="mb-1.5 block text-sm font-medium text-ink">
          {t('auth.code.label')}
        </label>
        <input
          ref={inputRef}
          id="email-code"
          type="text"
          inputMode="numeric"
          autoComplete="one-time-code"
          pattern="[0-9]*"
          maxLength={CODE_LENGTH}
          placeholder="••••••"
          value={code}
          disabled={verifying}
          onChange={(event) => {
            const next = event.target.value.replace(/\D/g, '').slice(0, CODE_LENGTH);
            setCode(next);
            if (error) setError(null);
            if (next.length === CODE_LENGTH) void verify(next);
          }}
          aria-invalid={error ? 'true' : 'false'}
          aria-describedby={error ? 'email-code-error' : undefined}
          className={
            'h-14 w-full rounded-xl border bg-white text-center font-mono text-2xl tracking-[0.5em] text-ink ' +
            'placeholder:text-line focus:outline-none focus:ring-2 disabled:opacity-60 ' +
            (error
              ? 'border-red-300 focus:border-red-400 focus:ring-red-100'
              : 'border-line focus:border-brand-400 focus:ring-brand-100')
          }
        />
        {error ? (
          <p id="email-code-error" role="alert" className="mt-1.5 text-sm font-medium text-red-600">
            {error}
          </p>
        ) : (
          <p className="mt-1.5 flex items-center gap-1.5 text-xs text-muted">
            <MailCheck className="size-3.5" aria-hidden />
            {t('auth.code.email_hint')}
          </p>
        )}
      </div>

      <Button type="submit" size="lg" className="w-full" disabled={verifying}>
        {verifying ? (
          <>
            <Spinner size="sm" /> {t('auth.code.verifying')}
          </>
        ) : (
          t('auth.code.submit')
        )}
      </Button>

      <p className="text-center text-sm text-muted" aria-live="polite">
        {t('auth.code.not_received')}{' '}
        {resendIn > 0 ? (
          <span>{t('auth.code.resend_in', { seconds: resendIn })}</span>
        ) : (
          <button
            type="button"
            onClick={() => void resend()}
            disabled={resending}
            className="font-semibold text-brand-600 hover:underline disabled:opacity-60"
          >
            {resending ? t('auth.common.sending') : t('auth.code.resend')}
          </button>
        )}
      </p>
    </form>
  );
}
